/**
 * React Query hooks for `/api/connectors`, `/api/connectors/:id/sync`,
 * `/api/connectors/:id/runs`, and the global adapter catalog.
 */

import { getToken } from "@cometchat-ai/auth/client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { AgentsApiError, readBaseUrl } from "../../agents/api/client";
import { camelizeKeys, snakeizeKeys } from "./camelize";
import { apiRequest } from "./client";
import type {
  AdapterSpec,
  Connection,
  CreateConnectionRequest,
  MappingFieldInput,
  Paged,
  SyncRun,
} from "./types";

/**
 * Multipart POST for the file-upload connector endpoints. The shared
 * {@link apiRequest} always JSON-encodes its body, so file uploads need their own
 * `fetch` that sends `FormData` (the browser sets the multipart boundary) with
 * the same bearer-token auth and error envelope.
 */
async function fileRequest<T>(path: string, form: FormData): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json" };
  const token = getToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const res = await fetch(`${readBaseUrl()}${path}`, { method: "POST", headers, body: form });
  const text = await res.text();
  const parsed = text.length > 0 ? (JSON.parse(text) as unknown) : undefined;
  if (!res.ok) {
    const env = (parsed ?? {}) as { error?: string; message?: string; request_id?: string };
    throw new AgentsApiError({
      status: res.status,
      code: env.error ?? `HTTP_${res.status}`,
      message: env.message ?? res.statusText ?? "Upload failed",
      requestId: env.request_id ?? "",
    });
  }
  return parsed as T;
}

export const connectorsKeys = {
  all: (slug: string) => ["connectors", slug] as const,
  list: (slug: string) => ["connectors", slug, "list"] as const,
  runs: (slug: string, id: string) => ["connectors", slug, "runs", id] as const,
  adapters: (slug: string) => ["connectors", slug, "adapters"] as const,
  capabilities: (slug: string) => ["connectors", slug, "capabilities"] as const,
};

export interface DataSourceCapabilities {
  /** Whether fast long-text (trigram) search/indexing is available on this DB. */
  trigramAvailable: boolean;
}

/**
 * Data-plane capabilities for the workspace. Drives the column "indexed" toggle
 * hint: when `trigramAvailable` is false, long-text columns can't be indexed for
 * fast search (the ingest path skips them rather than failing). Best-effort — a
 * fetch failure just leaves the hint unshown.
 */
export function useDataSourceCapabilities(slug: string) {
  return useQuery({
    queryKey: connectorsKeys.capabilities(slug),
    queryFn: () =>
      apiRequest<DataSourceCapabilities>("GET", "/api/connectors/capabilities").then(
        camelizeKeys<DataSourceCapabilities>,
      ),
    staleTime: 5 * 60 * 1000,
  });
}

export function useConnectors(slug: string) {
  return useQuery({
    queryKey: connectorsKeys.list(slug),
    queryFn: () =>
      // The API serves the workspace's connections as a bare array.
      apiRequest<Connection[]>("GET", "/api/connectors/connections").then(
        camelizeKeys<Connection[]>,
      ),
    // Poll while a connection is still settling so its status flips to
    // connected/error without a manual refresh; idle otherwise.
    refetchInterval: (query) => {
      const data = query.state.data as Connection[] | undefined;
      return data?.some((c) => c.status === "connecting") ? 3000 : false;
    },
  });
}

export function useAdapters(slug: string) {
  return useQuery({
    queryKey: connectorsKeys.adapters(slug),
    queryFn: () =>
      // The global connector catalog is a bare array at the collection root.
      apiRequest<AdapterSpec[]>("GET", "/api/connectors").then(camelizeKeys<AdapterSpec[]>),
  });
}

/**
 * Begin a managed-Airbyte OAuth connect: ask the API for the provider consent
 * URL, stash where to return, and send the browser to the provider. The API's
 * callback finishes the connect and 302s back to the dashboard with
 * `?connector=connected|error` (handled on landing).
 */
export function useInitiateOAuth() {
  return useMutation({
    mutationFn: ({
      adapterId,
      fields,
      name,
    }: {
      adapterId: string;
      fields: Record<string, string>;
      name?: string;
    }) =>
      // `fields` are opaque source config (keys are provider-defined) — sent
      // verbatim, not snake/camel-transformed.
      apiRequest<{ consent_url: string }>("POST", `/api/connectors/${adapterId}/oauth/initiate`, {
        fields,
        name,
      }).then((r) => r.consent_url),
    onSuccess: (consentUrl) => {
      sessionStorage.setItem("tables.oauthReturn", window.location.pathname);
      window.location.assign(consentUrl);
    },
  });
}

export function useCreateConnector(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateConnectionRequest) =>
      apiRequest<Connection>("POST", "/api/connectors", snakeizeKeys(body)).then(
        camelizeKeys<Connection>,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: connectorsKeys.list(slug) });
    },
  });
}

export interface DetectedField {
  path: string;
  type: "text" | "number" | "boolean" | "date";
  column: string;
}

/**
 * Sample a JSON URL → detected mappable fields (for the mapping editor). When a
 * secret is supplied, the request is signed server-side just like a real sync,
 * so HMAC-protected endpoints can be sampled before the connection is saved.
 */
export function useSampleJson() {
  return useMutation({
    mutationFn: (input: { url: string; adapterId?: string; secretKey?: string }) =>
      apiRequest<{ stream: string; fields: DetectedField[]; sample: unknown[] }>(
        "POST",
        "/api/connectors/sample-json",
        snakeizeKeys(input),
      ),
  });
}

/**
 * Sample an uploaded JSON file → detected mappable fields (for the mapping
 * editor). Mirrors {@link useSampleJson} but uploads the file as multipart.
 */
export function useSampleFile() {
  return useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append("file", file);
      return fileRequest<{ stream: string; fields: DetectedField[]; sample: unknown[] }>(
        "/api/connectors/sample-file",
        form,
      );
    },
  });
}

/**
 * Create a file-upload connection and import the uploaded JSON in one request.
 * The optional mapping is sent as a JSON string field alongside the file.
 */
export function useCreateFileConnector(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      name,
      file,
      mapping,
    }: {
      name: string;
      file: File;
      mapping?: MappingFieldInput[];
    }) => {
      const form = new FormData();
      form.append("file", file);
      form.append("name", name);
      if (mapping && mapping.length > 0) form.append("mapping", JSON.stringify(mapping));
      return fileRequest<Connection>("/api/connectors/file", form).then(camelizeKeys<Connection>);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: connectorsKeys.list(slug) });
      void qc.invalidateQueries({ queryKey: ["tables", slug] });
    },
  });
}

export function useDeleteConnector(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ connectionId, purge }: { connectionId: string; purge: boolean }) =>
      apiRequest<void>("DELETE", `/api/connectors/${connectionId}${purge ? "?purge=true" : ""}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: connectorsKeys.list(slug) });
      // Synced tables may have been dropped — refresh the tables list too.
      void qc.invalidateQueries({ queryKey: ["tables", slug] });
    },
  });
}

export function useConnectorRuns(slug: string, connectionId: string | undefined) {
  return useQuery({
    queryKey: connectorsKeys.runs(slug, connectionId ?? ""),
    queryFn: () =>
      apiRequest<Paged<SyncRun>>("GET", `/api/connectors/${connectionId}/runs`).then(
        camelizeKeys<Paged<SyncRun>>,
      ),
    enabled: Boolean(connectionId),
    // Poll while the latest run is in flight so it transitions to
    // succeeded/failed live (an Airbyte sync can take minutes).
    refetchInterval: (query) => {
      const data = query.state.data as Paged<SyncRun> | undefined;
      const latest = data?.items?.[0];
      return latest && (latest.status === "running" || latest.status === "queued") ? 3000 : false;
    },
  });
}

export function useTriggerSync(slug: string, connectionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiRequest<SyncRun>("POST", `/api/connectors/${connectionId}/sync`).then(
        camelizeKeys<SyncRun>,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: connectorsKeys.runs(slug, connectionId) });
      void qc.invalidateQueries({ queryKey: connectorsKeys.list(slug) });
      // A completed sync registers/updates tables — refresh the tables list.
      void qc.invalidateQueries({ queryKey: ["tables", slug] });
    },
  });
}
